import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, MessageCircle, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers / non-secure contexts
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
};

/** Reusable share control: Copy Link + Share on WhatsApp.
 * @param url        absolute URL to share
 * @param text       message text that accompanies the link on WhatsApp
 * @param testIdPrefix unique data-testid prefix (e.g. "job-share")
 * @param iconOnly   render a compact square icon button
 * @param label      button label when not iconOnly (default "Share")
 */
export const ShareButton = ({ url, text, testIdPrefix = "share", iconOnly = false, label = "Share", variant = "outline", size = "sm", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 900);
    } else {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant}
          size={iconOnly ? "icon" : size}
          className={`rounded-lg bg-white ${iconOnly ? "h-9 w-9 shrink-0" : ""} ${className}`}
          aria-label="Share"
          data-testid={`${testIdPrefix}-button`}
        >
          <Share2 className="h-4 w-4" />
          {!iconOnly && <span className="ml-1.5">{label}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2 rounded-xl" data-testid={`${testIdPrefix}-menu`}>
        <button
          type="button"
          onClick={handleCopy}
          data-testid={`${testIdPrefix}-copy`}
          className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted transition-colors text-left"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-muted-foreground" />}
          {copied ? "Copied!" : "Copy Link"}
        </button>
        <a
          href={whatsappHref}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testIdPrefix}-whatsapp`}
          onClick={() => setOpen(false)}
          className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted transition-colors"
        >
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          Share on WhatsApp
        </a>
      </PopoverContent>
    </Popover>
  );
};
